<script setup lang="ts">
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { fitFavoriteCount } from "~/code/sidebar-favorites-layout";
import { useSidebarFavorites } from "~/composables/useSidebarFavorites";
import SidebarIcon from "~/components/layout/full/vertical-sidebar/Icon.vue";
import pageRoutes, {
  buildSidebarMenuFromTree,
  fallbackPageRoutes,
  getEffectiveRouteAccess,
  loadSidebarItemsIntoPageRoutes,
  staticPageRoutes,
  setPageRoutes,
  withAdminManagementRoutes,
} from "~/code/page-routes";
import { Menu2Icon } from "vue-tabler-icons";
import { useAuthStore } from "~/store/auth-store";
import { useRolePermission } from "~/composables/useRolePermission";
import { useSidebarItemStore } from "~/store/sidebar-item-store";

const {
  computeRoles,
  computePermissions,
  computeRolesAndPermissions,
  userRoles,
  processedUserPermissions,
  isAdminUser,
  isImpersonating,
} = useRolePermission();

const authState = useAuthStore();
const sidebarItemStore = useSidebarItemStore();

const sidebarMenu = ref<typeof pageRoutes>([]);
const sDrawer = ref(true);
const sidebarLoaded = ref(false);
const openedGroups = ref<string[]>([]);
const ACCORDION_STORAGE_KEY_PREFIX = "sidebar-accordion-open";

type SidebarRoute = (typeof pageRoutes)[number];

interface AccordionSection {
  key: string;
  header: string;
  items: SidebarRoute[];
}

const currentUserStorageSuffix = computed(() => {
  const user = authState.user?.value as
    | Record<string, unknown>
    | null
    | undefined;

  if (!user) {
    return "anonymous";
  }

  const candidates = [
    user.id,
    user.userId,
    user.username,
    user.userName,
    user.email,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim().toLowerCase();
    }

    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return String(candidate);
    }
  }

  return "anonymous";
});

const accordionStorageKey = computed(
  () => `${ACCORDION_STORAGE_KEY_PREFIX}:${currentUserStorageSuffix.value}`,
);

const { favorites, moveFavorite, removeFavorite } = useSidebarFavorites(currentUserStorageSuffix);
const favoriteItems = computed(() => favorites.value.flatMap((path) => {
  const item = sidebarMenu.value.find((route) => route.to === path && !route.disabled);
  return item ? [item] : [];
}));
const draggedLink = ref<string | null>(null);
const draggingFavorite = ref(false);
const favoritesHovered = ref(false);
const favoritesExpanded = ref(true);
const favoritesMoreOpen = ref(false);
const favoriteLinksElement = ref<HTMLElement | null>(null);
const favoriteMeasureElement = ref<HTMLElement | null>(null);
const favoriteMoreMeasure = ref<HTMLElement | null>(null);
const favoriteVisibleCount = ref(0);
let favoritesResizeObserver: ResizeObserver | null = null;
const favoriteDropTarget = ref<string | null>(null);
const visibleFavoriteItems = computed(() => favoriteItems.value.slice(0, favoriteVisibleCount.value));
const overflowFavoriteItems = computed(() => favoriteItems.value.slice(favoriteVisibleCount.value));
function measureFavorites() {
  const available = favoriteLinksElement.value?.clientWidth || 0;
  if (!available || !favoriteMeasureElement.value) return;
  const widths = Array.from(favoriteMeasureElement.value.children).map((element) => element.getBoundingClientRect().width);
  favoriteVisibleCount.value = fitFavoriteCount(widths, available, favoriteMoreMeasure.value?.getBoundingClientRect().width || 80);
}
watch([favoriteItems, favoritesExpanded], async () => {
  await nextTick();
  measureFavorites();
}, { flush: "post" });
let linkDragPreview: HTMLElement | null = null;

function startLinkDrag(event: DragEvent, item: SidebarRoute, fromFavorites = false) {
  if (!item.to || item.disabled || !event.dataTransfer) { event.preventDefault(); return; }
  event.stopPropagation();
  draggedLink.value = item.to;
  draggingFavorite.value = fromFavorites;
  event.dataTransfer.effectAllowed = "copyMove";
  event.dataTransfer.setData("text/plain", item.to);
  linkDragPreview?.remove();
  linkDragPreview = document.createElement("div");
  linkDragPreview.textContent = item.title || item.to;
  Object.assign(linkDragPreview.style, {
    position: "fixed", top: "-1000px", left: "0", padding: "12px 20px",
    borderRadius: "0", background: "rgb(var(--v-theme-surface))",
    color: "rgb(var(--v-theme-on-surface))", border: "1px solid rgb(var(--v-theme-primary))",
    font: "14px sans-serif", width: "240px",
  });
  document.body.appendChild(linkDragPreview);
  event.dataTransfer.setDragImage?.(linkDragPreview, 20, 20);
}
function endLinkDrag() {
  linkDragPreview?.remove();
  linkDragPreview = null;
  draggedLink.value = null;
  draggingFavorite.value = false;
  favoritesHovered.value = false;
  favoriteDropTarget.value = null;
}
function dropFavorite(event: DragEvent, beforePath?: string) {
  event.preventDefault();
  event.stopPropagation();
  const path = draggedLink.value;
  if (path && sidebarMenu.value.some((item) => item.to === path && !item.disabled)) moveFavorite(path, beforePath);
  endLinkDrag();
}
function allowFavoriteDragOut(event: DragEvent) {
  if (draggingFavorite.value && draggedLink.value) {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
  }
}
function dropOutsideFavorites(event: DragEvent) {
  if (draggingFavorite.value && draggedLink.value) {
    event.preventDefault();
    removeFavorite(draggedLink.value);
    endLinkDrag();
  }
}
function sectionIcon(header: string) {
  const icons: Record<string, string> = {
    general: "mdi-shape", locations: "mdi-map-marker", "game content": "mdi-gamepad-variant",
    "json content": "mdi-code-json", content: "mdi-file-document-multiple", progression: "mdi-chart-timeline-variant",
    competition: "mdi-trophy", store: "mdi-store", "content management": "mdi-folder-cog",
    support: "mdi-lifebuoy", shared: "mdi-share-variant", system: "mdi-server",
    "access management": "mdi-account-key", administration: "mdi-shield-account",
  };
  return icons[header.toLowerCase()] || "mdi-folder-outline";
}

const readStoredOpenSections = (): string[] => {
  if (typeof window === "undefined") {
    return [];
  }

  const raw = localStorage.getItem(accordionStorageKey.value);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;

    if (Array.isArray(parsed)) {
      return parsed
        .filter((entry): entry is string => typeof entry === "string")
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
    }
  } catch {
    const trimmed = raw.trim();
    if (trimmed.length > 0) {
      return [trimmed];
    }
  }

  return [];
};

const persistOpenSections = (sectionKeys: string[]) => {
  if (typeof window === "undefined") {
    return;
  }

  if (sectionKeys.length === 0) {
    localStorage.removeItem(accordionStorageKey.value);
    return;
  }

  localStorage.setItem(accordionStorageKey.value, JSON.stringify(sectionKeys));
};

const normalizeSectionKey = (value: string): string => {
  return value.trim().toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
};

const accordionMenu = computed(() => {
  const sections: AccordionSection[] = [];
  const standaloneItems: SidebarRoute[] = [];

  let activeSection: AccordionSection | null = null;

  for (const item of sidebarMenu.value) {
    if (item.header) {
      activeSection = {
        key: `${normalizeSectionKey(item.header)}-${sections.length}`,
        header: item.header,
        items: [],
      };

      sections.push(activeSection);
      continue;
    }

    if (activeSection) {
      activeSection.items.push(item);
      continue;
    }

    standaloneItems.push(item);
  }

  return {
    sections: sections.filter((section) => section.items.length > 0),
    standaloneItems,
  };
});

console.log("authState.authenticated state:" + authState.authenticated.value);
console.log("your roles:" + JSON.stringify(authState.user?.value));

const refreshSidebarMenu = () => {
  const availableRoutes = isAdminUser.value && !isImpersonating.value
    ? withAdminManagementRoutes(pageRoutes)
    : pageRoutes;
  const filteredRoutes = availableRoutes.filter((route) => {
    if (isAdminUser.value && !isImpersonating.value) {
      return true;
    }

    const resolvedAccess = getEffectiveRouteAccess(route);
    const routeRoles = resolvedAccess?.roles;
    const routePermissions = resolvedAccess?.permissions;

    if (!routeRoles && !routePermissions) {
      return true;
    }

    if (!routeRoles && routePermissions) {
      return computePermissions(routePermissions);
    }

    if (routeRoles && !routePermissions) {
      return computeRoles(routeRoles);
    }

    if (routeRoles && routePermissions) {
      return computeRolesAndPermissions(routeRoles, routePermissions);
    }

    return false;
  });

  const prunedRoutes: typeof pageRoutes = [];

  for (let index = 0; index < filteredRoutes.length; index++) {
    const route = filteredRoutes[index];

    if (!route.header) {
      prunedRoutes.push(route);
      continue;
    }

    let hasVisibleChild = false;

    for (
      let lookAhead = index + 1;
      lookAhead < filteredRoutes.length;
      lookAhead++
    ) {
      const candidate = filteredRoutes[lookAhead];

      if (candidate.header) {
        break;
      }

      hasVisibleChild = true;
      break;
    }

    if (hasVisibleChild) {
      prunedRoutes.push(route);
    }
  }

  const dashboardRoute =
    pageRoutes.find((route) => route.to === "/") || fallbackPageRoutes[0];

  const routesWithoutDashboard = prunedRoutes.filter(
    (route) => route.to !== "/",
  );

  sidebarMenu.value = [dashboardRoute, ...routesWithoutDashboard];
};

const syncRoutesFromSidebarTree = () => {
  const dbRoutes = buildSidebarMenuFromTree(sidebarItemStore.sidebarTree || []);

  if (dbRoutes.length === 0) {
    setPageRoutes(staticPageRoutes);
    return;
  }

  const routesWithoutDashboard = dbRoutes.filter((route) => route.to !== "/");
  setPageRoutes([...fallbackPageRoutes, ...routesWithoutDashboard]);
};

onMounted(async () => {
  await nextTick();
  if (typeof ResizeObserver !== "undefined") {
    favoritesResizeObserver = new ResizeObserver(measureFavorites);
    if (favoriteLinksElement.value) favoritesResizeObserver.observe(favoriteLinksElement.value);
    if (favoriteMeasureElement.value) favoritesResizeObserver.observe(favoriteMeasureElement.value);
  }
  measureFavorites();
  document.addEventListener("dragover", allowFavoriteDragOut);
  document.addEventListener("drop", dropOutsideFavorites);
  await loadSidebarItemsIntoPageRoutes();
  refreshSidebarMenu();
  sidebarLoaded.value = true;
});

onBeforeUnmount(() => {
  favoritesResizeObserver?.disconnect();
  endLinkDrag();
  document.removeEventListener("dragover", allowFavoriteDragOut);
  document.removeEventListener("drop", dropOutsideFavorites);
});

watch(
  () => authState.user?.value,
  () => {
    refreshSidebarMenu();
  },
  { deep: true },
);

watch(
  () => authState.authenticated?.value,
  () => {
    refreshSidebarMenu();
  },
);

watch(
  [userRoles, processedUserPermissions],
  () => {
    refreshSidebarMenu();
  },
  { deep: true },
);

watch(
  () => sidebarItemStore.sidebarTree,
  () => {
    syncRoutesFromSidebarTree();
    refreshSidebarMenu();
  },
  { deep: true },
);

watch(
  () => accordionMenu.value.sections,
  (sections) => {
    if (sections.length === 0) {
      openedGroups.value = [];
      persistOpenSections([]);
      return;
    }

    const sectionKeys = new Set(sections.map((section) => section.key));
    const storedOpen = readStoredOpenSections().filter((key) =>
      sectionKeys.has(key),
    );

    if (storedOpen.length > 0) {
      openedGroups.value = storedOpen;
      return;
    }

    const existingOpen = openedGroups.value.filter((key) =>
      sectionKeys.has(key),
    );

    if (existingOpen.length > 0) {
      openedGroups.value = existingOpen;
      return;
    }

    openedGroups.value = sections.map((section) => section.key);
  },
  { deep: true },
);

watch(
  () => openedGroups.value,
  (value) => {
    persistOpenSections(value);
  },
  { deep: true },
);

watch(
  () => accordionStorageKey.value,
  () => {
    const sections = accordionMenu.value.sections;

    if (sections.length === 0) {
      openedGroups.value = [];
      return;
    }

    const sectionKeys = new Set(sections.map((section) => section.key));
    const storedOpen = readStoredOpenSections().filter((key) =>
      sectionKeys.has(key),
    );

    if (storedOpen.length > 0) {
      openedGroups.value = storedOpen;
      return;
    }

    openedGroups.value = sections.map((section) => section.key);
  },
);
</script>

<template>
  <v-navigation-drawer
    left
    elevation="0"
    app
    class="leftSidebar"
    v-model="sDrawer"
  >
    <div class="pa-5">
      <LayoutFullLogo />
    </div>

    <div>
      <perfect-scrollbar class="scrollnavbar">
        <v-list
          v-model:opened="openedGroups"
          open-strategy="multiple"
          class="pa-6"
        >
          <template v-if="sidebarLoaded">
      <v-btn to="/" prepend-icon="mdi-home" variant="text" block :rounded="false">Dashboard</v-btn>
      <v-btn class="sidebar-collapse-headings mb-3" block variant="tonal" size="small" :rounded="false"
        :prepend-icon="openedGroups.length > 0 ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal'"
        :disabled="accordionMenu.sections.length === 0"
        @click="openedGroups = openedGroups.length > 0 ? [] : accordionMenu.sections.map(section => section.key)">
        {{ openedGroups.length > 0 ? 'Collapse headings' : 'Expand headings' }}
      </v-btn>

            <LayoutFullVerticalSidebarNavItem
              v-for="item in accordionMenu.standaloneItems.filter(item => item.to !== '/')"
              :key="item.to || item.title" :item="item" class="leftPadding"
              :draggable="Boolean(item.to && !item.disabled)"
              @dragstart="startLinkDrag($event, item)" @dragend="endLinkDrag" />

            <template
              v-for="section in accordionMenu.sections"
              :key="section.key"
            >
              <v-list-group :value="section.key" collapse-icon="mdi-chevron-down" expand-icon="mdi-chevron-right">
                <template #activator="{ props: activatorProps }">
                  <v-list-item
                    v-bind="activatorProps"
                    :prepend-icon="sectionIcon(section.header)"
                    color="darkText"
                    class="sidebar-section-activator smallCap text-uppercase text-subtitle-2 font-weight-bold mt-1"
                  >
                    <v-list-item-title>{{ section.header }}</v-list-item-title>
                  </v-list-item>
                </template>

                <LayoutFullVerticalSidebarNavItem
                  v-for="(item, index) in section.items"
                  :key="item.title || item.to || `${section.key}-${index}`"
                  :item="item"
                  class="leftPadding"
                  :draggable="Boolean(item.to && !item.disabled)"
                  @dragstart="startLinkDrag($event, item)" @dragend="endLinkDrag"
                />
              </v-list-group>
            </template>

            <template
              v-if="
                accordionMenu.sections.length === 0 &&
                accordionMenu.standaloneItems.length === 0
              "
            >
              <div class="text-medium-emphasis text-caption pa-2">
                No menu items available.
              </div>
            </template>
          </template>

          <template v-else>
            <div class="d-flex justify-center align-center pa-5">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </div>
          </template>
        </v-list>
      </perfect-scrollbar>
    </div>
  </v-navigation-drawer>

  <v-app-bar elevation="0" height="70">
    <div class="d-flex align-center ga-2 w-100 px-3">
      <v-btn class="hidden-lg-and-up" @click="sDrawer = !sDrawer" icon variant="flat" size="small">
        <Menu2Icon size="20" stroke-width="1.5" />
      </v-btn>

            <div class="sidebar-favorites"
              :class="{ 'sidebar-favorites--active': draggedLink, 'sidebar-favorites--hovered': favoritesHovered }"
              @dragover.prevent="favoritesHovered = Boolean(draggedLink)"
              @dragleave="favoritesHovered = false"
              @drop="dropFavorite">
              <button type="button" class="sidebar-favorites-heading d-flex align-center text-subtitle-2 font-weight-bold pa-2"
                :aria-expanded="favoritesExpanded || Boolean(draggedLink)" aria-controls="sidebar-favorites-links"
                @click="favoritesExpanded = !favoritesExpanded">
                <v-icon start color="amber">mdi-star</v-icon>
                Favorites
                <v-icon class="ml-auto" size="small">{{ favoritesExpanded || draggedLink ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              </button>
              <div ref="favoriteLinksElement" id="sidebar-favorites-links" v-show="favoritesExpanded || draggedLink">
              <div v-if="favoriteItems.length === 0" class="text-caption text-medium-emphasis pa-2">
                Drag links here to add favorites.
              </div>
              <div class="sidebar-favorites-bar">
              <v-list-item v-for="item in visibleFavoriteItems" :key="item.to" :to="item.to"
                :rounded="false" color="primary" active-class="sidebar-route-active" draggable="true"
                :class="{ 'favorite-drop-target': favoriteDropTarget === item.to }"
                @dragover.prevent.stop="favoriteDropTarget = item.to"
                @dragleave="favoriteDropTarget = null"
                @drop="dropFavorite($event, item.to)"
                :target="item.type === 'external' ? '_blank' : ''"
                @dragstart="startLinkDrag($event, item, true)" @dragend="endLinkDrag">
                <template #prepend><SidebarIcon :item="item.icon" /></template>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
              </div>
              <v-menu v-if="overflowFavoriteItems.length" v-model="favoritesMoreOpen" location="bottom end">
                <template #activator="{ props: menuProps }">
                  <button v-bind="menuProps" type="button" class="sidebar-favorites-more pa-2"
                    @dragover.prevent="favoritesMoreOpen = true">
                    More <v-icon size="small">mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list class="favorites-overflow-menu" @dragover.prevent @drop="dropFavorite">
                  <v-list-item v-for="item in overflowFavoriteItems" :key="item.to" :to="item.to"
                    :rounded="false" active-class="sidebar-route-active" draggable="true"
                    :class="{ 'favorite-drop-target': favoriteDropTarget === item.to }"
                    :target="item.type === 'external' ? '_blank' : ''"
                    @dragover.prevent.stop="favoriteDropTarget = item.to"
                    @dragleave="favoriteDropTarget = null" @drop="dropFavorite($event, item.to)"
                    @dragstart="startLinkDrag($event, item, true)" @dragend="endLinkDrag">
                    <template #prepend><SidebarIcon :item="item.icon" /></template>
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <div v-if="draggingFavorite" class="favorites-drag-hint text-caption pa-2">Drop outside this area to remove.</div>
              </div>
            </div>
      <div class="favorites-measure" aria-hidden="true" inert>
        <div ref="favoriteMeasureElement" class="sidebar-favorites-bar">
          <v-list-item v-for="item in favoriteItems" :key="item.to" :rounded="false">
            <template #prepend><SidebarIcon :item="item.icon" /></template>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </div>
        <button ref="favoriteMoreMeasure" class="sidebar-favorites-more pa-2" type="button" tabindex="-1">
          More <v-icon size="small">mdi-chevron-down</v-icon>
        </button>
      </div>

      <LayoutFullVerticalHeaderNotificationDD />
      <LayoutFullVerticalHeaderProfileDD />
    </div>
  </v-app-bar>
</template>

<style scoped>
.favorites-measure { position: fixed; left: -10000px; top: 0; visibility: hidden; display: flex; width: max-content; pointer-events: none; }
.favorites-measure :deep(.v-list-item-title) { white-space: nowrap; font-size: 0.875rem; }
.favorites-measure :deep(.v-list-item__spacer) { width: 6px; }
.favorites-overflow-menu { max-height: 360px; overflow-y: auto; }
.favorites-overflow-menu :deep(.v-list-item-title) { white-space: normal; overflow-wrap: anywhere; }


.sidebar-favorites-bar { display: flex; gap: 2px; overflow: hidden; min-width: 0; flex: 1; }
.sidebar-favorites-bar .v-list-item { flex: 0 0 auto; min-height: 40px; }
.sidebar-favorites :deep(.v-list-item-title) { white-space: nowrap; text-overflow: clip; overflow-wrap: anywhere; font-size: 0.875rem; }
.sidebar-favorites :deep(.v-list-item__spacer) { width: 6px; }
.sidebar-favorites .v-list-item { background: rgb(var(--v-theme-sidebarItemBg)); }
.sidebar-favorites .v-list-item:hover { background: rgb(var(--v-theme-sidebarItemHoverBg)); }
.sidebar-favorites .sidebar-route-active { background: rgb(var(--v-theme-sidebarItemActiveBg)); box-shadow: inset 3px 0 rgb(var(--v-theme-error)); }
#sidebar-favorites-links { display: flex; align-items: center; min-width: 0; flex: 1; }
.sidebar-favorites-more { flex-shrink: 0; }
.favorites-drag-hint { position: absolute; top: 100%; background: rgb(var(--v-theme-surface)); }

.sidebar-favorites-more { color: rgb(var(--v-theme-error)); font-weight: 600; text-decoration: underline; }
.favorite-drop-target { box-shadow: inset 3px 0 rgb(var(--v-theme-error)); outline: 1px solid rgb(var(--v-theme-error)); }

.sidebar-favorites {
  display: flex; align-items: center; position: relative; flex: 1; min-width: 0; margin: 0;
  background: rgba(var(--v-theme-warning), 0.12);
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 0;
  transition: background-color 0.15s, border-color 0.15s;
}
.sidebar-favorites-heading {
  flex-shrink: 0;
  text-align: left;
  color: inherit;
  background: rgba(var(--v-theme-warning), 0.28);
  border-radius: 0;
  min-height: 40px;
  font-size: 1rem !important;
}
.sidebar-section-activator {
  background: rgb(var(--v-theme-sidebarHeadingBg));
  border-radius: 0;
}
.sidebar-favorites--active {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.12);
}
.sidebar-favorites--hovered {
  background: rgba(var(--v-theme-primary), 0.25);
  outline: 2px solid rgb(var(--v-theme-primary));
}
[draggable="true"] { cursor: grab; }
</style>
